// Styled QR rendering on top of the `qrcode` library (spec-compliant encoding,
// any length, real error-correction levels). Produces a standalone SVG string
// that is also used for PNG export and for the in-browser scan check.
import QRCode from "qrcode";

export const PATTERNS = [
  { id: "square", label: "Square" },
  { id: "rounded", label: "Rounded" },
  { id: "dots", label: "Dots" },
];
export const CORNERS = [
  { id: "square", label: "Square" },
  { id: "rounded", label: "Rounded" },
  { id: "circle", label: "Circle" },
];
export const PRESETS = [
  { id: "classic", label: "Classic", fg: "#000000", bg: "#FFFFFF" },
  { id: "instapass", label: "InstaPass", fg: "#E52324", bg: "#FFFFFF" },
  { id: "midnight", label: "Midnight", fg: "#0B0F19", bg: "#FFFFFF" },
  { id: "royal", label: "Royal", fg: "#3B2A8C", bg: "#F6F3FF" },
  { id: "forest", label: "Forest", fg: "#0F5132", bg: "#F1FAF4" },
  { id: "night", label: "Night", fg: "#FFFFFF", bg: "#0B0F19" },
];

const QUIET = 4; // modules of white space around the code (spec minimum)
const esc = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** Scannability warnings for a colour pair. */
export function colorAdvice(fg, bg) {
  const a = luminance(fg);
  const b = luminance(bg);
  const ratio = (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  if (ratio < 3) return { level: "error", text: "Not enough contrast between the two colors — phones may not read it." };
  if (a > b) return { level: "warn", text: "Light code on a dark background: newer phones read it, some older scanners don't." };
  if (ratio < 4.5) return { level: "warn", text: "Contrast is on the low side. Test it on paper before printing a lot." };
  return null;
}

function finder(x, y, style, fg, bg) {
  // 7x7 outer ring + 3x3 eye, at module coords (x, y)
  if (style === "circle") {
    return `<circle cx="${x + 3.5}" cy="${y + 3.5}" r="3" fill="none" stroke="${fg}" stroke-width="1"/>` +
      `<circle cx="${x + 3.5}" cy="${y + 3.5}" r="1.5" fill="${fg}"/>`;
  }
  const r1 = style === "rounded" ? 1.6 : 0;
  const r2 = style === "rounded" ? 0.8 : 0;
  return `<rect x="${x + 0.5}" y="${y + 0.5}" width="6" height="6" rx="${r1}" fill="none" stroke="${fg}" stroke-width="1"/>` +
    `<rect x="${x + 2}" y="${y + 2}" width="3" height="3" rx="${r2}" fill="${fg}"/>`;
}

/**
 * opts: { payload, fg, bg, pattern, corner, logo: null | "instapass" | dataURL, label }
 * Returns { svg, modules, version, size } or throws if the text is too long.
 */
export function renderQrSvg(opts) {
  const { payload, fg = "#000000", bg = "#FFFFFF", pattern = "square", corner = "square", logo = null, label = "" } = opts;
  const qr = QRCode.create(payload, { errorCorrectionLevel: logo ? "H" : "M" });
  const n = qr.modules.size;
  const data = qr.modules.data;
  const total = n + QUIET * 2;

  // Area left clear for a logo: ~22% of the code's width, well inside what
  // level-H error correction recovers.
  let hole = null;
  if (logo) {
    let side = Math.round(n * 0.22);
    if ((n - side) % 2) side += 1;
    const start = (n - side) / 2;
    hole = { start, end: start + side, side };
  }
  const inFinder = (r, c) => (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
  const inHole = (r, c) => hole && r >= hole.start && r < hole.end && c >= hole.start && c < hole.end;

  const parts = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!data[r * n + c] || inFinder(r, c) || inHole(r, c)) continue;
      const x = c + QUIET;
      const y = r + QUIET;
      if (pattern === "dots") parts.push(`<circle cx="${x + 0.5}" cy="${y + 0.5}" r="0.5"/>`);
      else if (pattern === "rounded") parts.push(`<rect x="${x}" y="${y}" width="1.02" height="1.02" rx="0.28"/>`);
      else parts.push(`<rect x="${x}" y="${y}" width="1.02" height="1.02"/>`);
    }
  }
  const finders = [
    finder(QUIET, QUIET, corner, fg, bg),
    finder(QUIET + n - 7, QUIET, corner, fg, bg),
    finder(QUIET, QUIET + n - 7, corner, fg, bg),
  ].join("");

  let logoSvg = "";
  if (hole) {
    const pad = 0.6;
    const lx = QUIET + hole.start + pad;
    const size = hole.side - pad * 2;
    if (logo === "instapass") {
      logoSvg =
        `<rect x="${lx}" y="${lx}" width="${size}" height="${size}" rx="${size * 0.22}" fill="#E52324"/>` +
        `<text x="${lx + size / 2}" y="${lx + size * 0.68}" text-anchor="middle" fill="#FFFFFF" font-size="${size * 0.5}" font-weight="900" font-family="Arial, Helvetica, sans-serif">IP</text>`;
    } else {
      logoSvg =
        `<rect x="${lx - 0.2}" y="${lx - 0.2}" width="${size + 0.4}" height="${size + 0.4}" rx="${size * 0.18}" fill="${bg}"/>` +
        `<image href="${esc(logo)}" x="${lx}" y="${lx}" width="${size}" height="${size}" preserveAspectRatio="xMidYMid meet"/>`;
    }
  }

  // Optional caption band under the code ("SCAN FOR TICKETS").
  const text = String(label || "").trim().slice(0, 32);
  const band = text ? Math.max(7, Math.round(total * 0.2)) : 0;
  const H = text ? total + band + QUIET / 2 : total;
  let caption = "";
  if (text) {
    const T = text.toUpperCase();
    const maxW = total - QUIET * 2;
    // Arial caps average ~0.68em wide (plus tracking); shrink to fit the width.
    const fs = Math.min(band * 0.46, maxW / (T.length * 0.72));
    caption =
      `<rect x="${QUIET}" y="${total}" width="${maxW}" height="${band}" rx="${band * 0.22}" fill="${fg}"/>` +
      `<text x="${total / 2}" y="${total + band / 2 + fs * 0.36}" text-anchor="middle" fill="${bg}" font-size="${fs.toFixed(2)}" font-weight="700" letter-spacing="${(fs * 0.04).toFixed(2)}" font-family="Arial, Helvetica, sans-serif">${esc(T)}</text>`;
  }

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${H}" width="${total * 10}" height="${H * 10}" shape-rendering="${pattern === "square" ? "crispEdges" : "geometricPrecision"}">` +
    `<rect width="${total}" height="${H}" fill="${bg}"/>` +
    `<g fill="${fg}">${parts.join("")}</g>${finders}${logoSvg}${caption}</svg>`;

  return { svg, version: qr.version, size: n, ecLevel: logo ? "H" : "M", aspect: H / total };
}

/** Draw an SVG string onto a canvas `width` px wide. Resolves the canvas. */
export function svgToCanvas(svg, width, aspect = 1) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = Math.round(width * aspect);
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/**
 * Reads the rendered code back with jsQR, the way a phone would.
 * Resolves { ok, decoded } — ok means it decoded to exactly `payload`.
 */
export async function scanCheck(svg, aspect, payload) {
  const { default: jsQR } = await import("jsqr");
  for (const w of [360, 720]) {
    const canvas = await svgToCanvas(svg, w, aspect);
    const ctx = canvas.getContext("2d");
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const res = jsQR(img.data, img.width, img.height, { inversionAttempts: "attemptBoth" });
    if (res) return { ok: res.data === payload, decoded: res.data };
  }
  return { ok: false, decoded: null };
}

export async function downloadPng(svg, aspect, name, width = 1200) {
  const canvas = await svgToCanvas(svg, width, aspect);
  await new Promise((resolve) =>
    canvas.toBlob((blob) => {
      triggerDownload(URL.createObjectURL(blob), `${name}.png`);
      resolve();
    }, "image/png")
  );
}

export function downloadSvg(svg, name) {
  triggerDownload(URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" })), `${name}.svg`);
}

function triggerDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
